import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { storesApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import type { Account } from '@motra/shared';

interface TaxEntry {
  name: string;
  value: number;
}

interface CmField {
  key: string;
  label: string;
  type?: string;
  required?: boolean;
}

export default function GeneralSettings() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);

  // Parse existing config
  const [paymentTypes, setPaymentTypes] = useState<string[]>([]);
  const [taxes, setTaxes] = useState<TaxEntry[]>([]);
  const [invCategories, setInvCategories] = useState<string[]>([]);
  const [cmFields, setCmFields] = useState<CmField[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // New item inputs
  const [newPaymentType, setNewPaymentType] = useState('');
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxValue, setNewTaxValue] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');

  useEffect(() => {
    if (!account) return;
    try {
      setPaymentTypes(account.payment_types ? account.payment_types.split(';') : []);
    } catch { /* empty */ }
    try {
      setInvCategories(account.inv_type ? JSON.parse(account.inv_type) : []);
    } catch { /* empty */ }
    try {
      setCmFields(account.cm_field ? JSON.parse(account.cm_field) : []);
    } catch { /* empty */ }
  }, [account]);

  async function handleSave() {
    if (!account?.id) return;
    setSaving(true);
    setError('');
    setSuccess(false);

    const payload: Partial<Account> = {
      payment_types: paymentTypes.join(';'),
      inv_type: JSON.stringify(invCategories),
      cm_field: JSON.stringify(cmFields),
    };

    try {
      await storesApi.updateGeneral(account.id, payload);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">General Settings</h1>
      </div>

      {/* Payment types */}
      <div className="settings-section form-card">
        <h3 className="form-section-title">{t('payment_type', 'Payment Types')}</h3>
        <div className="tag-list">
          {paymentTypes.map((pt) => (
            <span key={pt} className="tag">
              {pt}
              <button
                className="tag__remove"
                onClick={() => setPaymentTypes(paymentTypes.filter((p) => p !== pt))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
        <div className="tag-add-row">
          <input
            type="text"
            className="form-input form-input--sm"
            value={newPaymentType}
            onChange={(e) => setNewPaymentType(e.target.value)}
            placeholder="e.g. grabpay"
          />
          <button
            className="btn btn--sm btn--outline"
            onClick={() => {
              if (newPaymentType && !paymentTypes.includes(newPaymentType)) {
                setPaymentTypes([...paymentTypes, newPaymentType]);
                setNewPaymentType('');
              }
            }}
          >
            {t('add', 'Add')}
          </button>
        </div>
      </div>

      {/* Inventory categories */}
      <div className="settings-section form-card">
        <h3 className="form-section-title">Inventory Categories</h3>
        <div className="tag-list">
          {invCategories.map((cat) => (
            <span key={cat} className="tag">
              {cat}
              <button
                className="tag__remove"
                onClick={() => setInvCategories(invCategories.filter((c) => c !== cat))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
        <div className="tag-add-row">
          <input
            type="text"
            className="form-input form-input--sm"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="Category name"
          />
          <button
            className="btn btn--sm btn--outline"
            onClick={() => {
              if (newCategory && !invCategories.includes(newCategory)) {
                setInvCategories([...invCategories, newCategory]);
                setNewCategory('');
              }
            }}
          >
            {t('add', 'Add')}
          </button>
        </div>
      </div>

      {/* Customer fields */}
      <div className="settings-section form-card">
        <h3 className="form-section-title">Customer Field Definitions</h3>
        <div className="cm-fields-list">
          {cmFields.map((field, i) => (
            <div key={i} className="cm-field-row">
              <span className="cm-field-key">{field.key}</span>
              <span className="cm-field-label">{field.label}</span>
              <span className="cm-field-type">{field.type ?? 'text'}</span>
              <button
                className="btn btn--sm btn--danger"
                onClick={() => setCmFields(cmFields.filter((_, idx) => idx !== i))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <div className="tag-add-row">
          <input
            type="text"
            className="form-input form-input--sm"
            value={newFieldKey}
            onChange={(e) => setNewFieldKey(e.target.value)}
            placeholder="Field key (e.g. name)"
          />
          <input
            type="text"
            className="form-input form-input--sm"
            value={newFieldLabel}
            onChange={(e) => setNewFieldLabel(e.target.value)}
            placeholder="Label (e.g. Full Name)"
          />
          <button
            className="btn btn--sm btn--outline"
            onClick={() => {
              if (newFieldKey && newFieldLabel) {
                setCmFields([...cmFields, { key: newFieldKey, label: newFieldLabel }]);
                setNewFieldKey('');
                setNewFieldLabel('');
              }
            }}
          >
            {t('add', 'Add')}
          </button>
        </div>
      </div>

      {error && <div className="alert alert--error">{error}</div>}
      {success && <div className="alert alert--success">Settings saved successfully.</div>}

      <div className="form-actions">
        <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : t('save', 'Save All')}
        </button>
      </div>
    </div>
  );
}
